import { useCallback, useEffect, useRef, useState } from "react";
import { authedFetch } from "../auth/authedFetch";
import { CHAT_API_BASE_URL } from "../services/chatService";
import { debug } from "../utils/debugLogger";

/**
 * Speak instead of type, anywhere in the app.
 *
 * Lifted out of ChatInputArea, which had this welded into the composer and its
 * send flow. The card capture panel needs the same thing for the "where did you
 * meet" note: an owner standing in a car park after a networking event is not
 * going to type, and a typed note was the last thing standing between a scanned
 * card and a personal email (SGL-346).
 *
 * Two paths, same as the composer:
 *  - Chrome and Edge expose SpeechRecognition, so text appears live as they talk.
 *  - Safari, Firefox and iOS do not, so we record and post the audio to
 *    /chat/transcribe when they stop. Nothing appears until then, which is why
 *    `isTranscribing` exists: the UI has to say something is happening.
 *
 * Text is appended to whatever is already in the field, never replacing it.
 */
type Options = {
  /** Current field value; dictation appends to it. */
  value: string;
  /** Called with the full new value, live on Chrome and once on Safari. */
  onChange: (text: string) => void;
  /** Optional: called once when a dictation finishes, with the final value. */
  onComplete?: (text: string) => void;
  language?: string;
};

export function useVoiceDictation({ value, onChange, onComplete, language = "en-US" }: Options) {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  // The field value at the moment recording started. Read from a ref, not the
  // closure, or a long dictation appends to a stale prefix.
  const prefixRef = useRef("");

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  // A component can unmount mid-recording (panel closed, tab switched). Without
  // this the microphone stays on and the browser keeps showing the recording dot.
  useEffect(() => stop, [stop]);

  const transcribe = useCallback(
    async (blob: Blob, mimeType: string) => {
      setIsTranscribing(true);
      try {
        const ext = mimeType.includes("ogg") ? "ogg" : mimeType.includes("mp4") ? "mp4" : "webm";
        const form = new FormData();
        form.append("file", blob, `audio.${ext}`);
        form.append("language", language.split("-")[0]);
        const resp = await authedFetch(`${CHAT_API_BASE_URL}/chat/transcribe`, {
          method: "POST",
          body: form,
        });
        if (!resp.ok) throw new Error(`Transcription failed: ${resp.status}`);
        const data = await resp.json();
        const text = (data.text || "").trim();
        if (!text) {
          setError("Nothing was picked up. Try again a little closer to the microphone.");
          return;
        }
        const combined = prefixRef.current ? `${prefixRef.current} ${text}` : text;
        onChange(combined);
        onComplete?.(combined);
      } catch (err) {
        debug.error("Transcription error:", err);
        setError("Could not transcribe that. Type it instead, or try again.");
      } finally {
        setIsTranscribing(false);
      }
    },
    [language, onChange, onComplete]
  );

  const start = useCallback(() => {
    setError(null);
    prefixRef.current = (value || "").trim();
    const prefix = prefixRef.current ? prefixRef.current + " " : "";
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (SR) {
      const recognition = new SR();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = language;
      let finalTranscript = "";

      recognition.onresult = (event: any) => {
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) finalTranscript += event.results[i][0].transcript;
          else interim += event.results[i][0].transcript;
        }
        onChange(prefix + finalTranscript + interim);
      };

      recognition.onend = () => {
        setIsRecording(false);
        recognitionRef.current = null;
        const combined = (prefix + finalTranscript).trim();
        if (combined) {
          onChange(combined);
          onComplete?.(combined);
        }
      };

      recognition.onerror = (event: any) => {
        // "no-speech" fires whenever someone opens the mic and pauses; it is not
        // an error worth showing anyone.
        if (event.error !== "no-speech") {
          debug.error("Speech recognition error:", event.error);
          setError(
            event.error === "not-allowed"
              ? "Microphone access was blocked. Allow it in your browser settings."
              : "Could not hear that. Try again."
          );
        }
        setIsRecording(false);
        recognitionRef.current = null;
      };

      recognition.start();
      recognitionRef.current = recognition;
      setIsRecording(true);
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        const mimeType = MediaRecorder.isTypeSupported("audio/webm; codecs=opus")
          ? "audio/webm; codecs=opus"
          : MediaRecorder.isTypeSupported("audio/ogg; codecs=opus")
          ? "audio/ogg; codecs=opus"
          : "audio/mp4";
        const recorder = new MediaRecorder(stream, { mimeType });
        chunksRef.current = [];
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };
        recorder.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          setIsRecording(false);
          mediaRecorderRef.current = null;
          const blob = new Blob(chunksRef.current, { type: mimeType });
          chunksRef.current = [];
          transcribe(blob, mimeType);
        };
        recorder.start();
        mediaRecorderRef.current = recorder;
        setIsRecording(true);
      })
      .catch((err) => {
        debug.error("Microphone access denied:", err);
        setError("Microphone access was blocked. Allow it in your browser settings.");
      });
  }, [value, language, onChange, onComplete, transcribe]);

  const toggle = useCallback(() => {
    if (isRecording) stop();
    else start();
  }, [isRecording, start, stop]);

  return { isRecording, isTranscribing, error, start, stop, toggle };
}
